/**
 * 특송운임 계산기 (?page=freight) — 공개 도구. TRADEVERSE AMKO 요율(DHL/FEDEX/UPS/UNIPASS).
 * API: GET /api/tools/courier-freight{,/meta,/table}. 포털 셸(좌측 없음, 본문 중앙).
 */
import React, { useState, useEffect, useMemo } from "react";
import ApiPortalLayout from "./ApiPortalLayout";
import COUNTRY_KO from "./countryKo";

const TYPE_LABEL = {
  COMPARE: "전체 비교",
  DHL: "DHL", FEDEX: "FEDEX", UPS: "UPS",
  UNIPASS: "탁송품 과세운임", POST: "선편소포",
};
// 공식 확인 링크 — 최종 확정은 각 기관·특송사 공식 채널에서
const OFFICIAL_LINKS = {
  DHL: ["DHL 공식 견적", "https://www.dhl.com/kr-ko/home/get-a-quote.html"],
  FEDEX: ["FedEx 요율 확인", "https://www.fedex.com/ko-kr/online/rating.html"],
  // UPS 는 계산기 딥링크가 수시로 변경(404) — 항상 살아있는 코리아 홈으로
  UPS: ["UPS 코리아(배송 비용 계산)", "https://www.ups.com/kr/ko/home"],
  UNIPASS: ["근거 법령(관세법 시행규칙)", "https://www.law.go.kr/법령/관세법시행규칙"],
  POST: ["우체국 국제우편 요금안내", "https://ems.epost.go.kr/comm.RetrievePostagePriceNew.postal"],
};
const TYPE_DESC = {
  COMPARE: "국가·중량 한 번 입력으로 특송 3사(DHL·FEDEX·UPS)와 탁송품 과세운임·선편소포 요금을 나란히 비교합니다. 실제 청구액은 계약 조건에 따라 다르므로 공식 확인 링크에서 최종 확정하세요.",
  UNIPASS: "수입신고 시 운임을 알 수 없을 때 적용하는 관세청 고시 과세운임입니다 — 관세법시행규칙 제4조의3 제1항 제4호, 관세평가 운영에 관한 고시 제24조 제4항 제8호.",
  POST: "국제우편(선편 일반소포) 요금표 기준입니다 — 관세법시행령 제20조, 시행규칙 제4조의3. 최대 20kg까지 취급합니다.",
};

const S = {
  page: { maxWidth: 880, color: "#1f2937",
    fontFamily: "'Inter','Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  card: { border: "1px solid #e5e7eb", borderRadius: 12, padding: "18px 20px", marginTop: 16 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 },
  // 컨트롤 높이 44px 로 전부 통일 — select/input/button 이 제각각이면 폼이 삐뚤어 보임
  input: { width: "100%", height: 44, padding: "0 12px", fontSize: 14, borderRadius: 8,
    border: "1px solid #d1d5db", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box" },
  btn: { background: "#111827", color: "#fff", border: "none", borderRadius: 8,
    height: 44, padding: "0 22px", fontSize: 14, fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", display: "inline-flex", alignItems: "center",
    justifyContent: "center", boxSizing: "border-box" },
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 12.5, color: "#475569", borderRadius: 4, margin: "14px 0 0" },
  // sticky 헤더 — 스크롤러(패딩 0) 기준 top:0 고정. 배경·그림자·zIndex 로 행 비침 차단.
  th: { textAlign: "right", padding: "8px 10px", whiteSpace: "nowrap", fontSize: 12,
    fontWeight: 700, color: "#374151", position: "sticky", top: 0, zIndex: 2,
    background: "#f8fafc", boxShadow: "inset 0 -2px 0 #e5e7eb" },
  td: { textAlign: "right", padding: "5px 10px", borderBottom: "1px solid #f3f4f6",
    fontSize: 12, fontFamily: "ui-monospace,monospace", whiteSpace: "nowrap",
    background: "#fff" },
};
const won = (n) => (Number(n) || 0).toLocaleString("ko-KR") + "원";
const fmtDt = (d) => (d && d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}` : d || "");

export default function CourierFreightPage({ onNavigate, embedded = false }) {
  const [meta, setMeta] = useState(null); // {carriers:[{type,base_dt,fuel_per,countries[]}]}
  const [type, setType] = useState("COMPARE");
  const [cmp, setCmp] = useState(null); // 전체 비교 결과
  const [natInput, setNatInput] = useState("");
  const [weight, setWeight] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [table, setTable] = useState(null); // {type, base_dt, rows[]}
  const [showZones, setShowZones] = useState(false); // 적용지역별 국가명
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    fetch("/api/tools/courier-freight/meta")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMeta(d))
      .catch(() => setMeta({ carriers: [] }));
  }, []);

  const carrier = useMemo(
    () => meta?.carriers?.find((c) => c.type === type) || null,
    [meta, type],
  );
  // 국가 자동완성 — 라벨 "한글명 — 영문명 (코드)" ↔ nat 코드 해석 (한글/영문/코드 모두 허용)
  // ⚠ 네이티브 <datalist> 는 한글 IME 조합 중 클릭 선택이 불안정(Chrome) → 커스텀 드롭다운.
  const natOptions = useMemo(() => {
    if (type !== "COMPARE") return carrier?.countries || [];
    const seen = new Map();
    for (const c of meta?.carriers || []) {
      for (const n of c.countries) if (!seen.has(n.nat)) seen.set(n.nat, n);
    }
    return [...seen.values()].sort((a, b) => (a.nat_nm || "").localeCompare(b.nat_nm || ""));
  }, [type, carrier, meta]);
  const natLabel = (c) => {
    const ko = COUNTRY_KO[c.nat];
    return `${ko ? ko + " — " : ""}${c.nat_nm} (${c.nat})`;
  };
  const [natOpen, setNatOpen] = useState(false);
  const [natHi, setNatHi] = useState(-1); // 키보드 하이라이트 index
  const natMatches = useMemo(() => {
    const raw = natInput.trim();
    const t = raw.toUpperCase();
    if (!raw) return natOptions.slice(0, 12);
    return natOptions
      .filter(
        (c) =>
          (COUNTRY_KO[c.nat] || "").includes(raw) ||
          (c.nat_nm || "").toUpperCase().includes(t) ||
          c.nat.startsWith(t),
      )
      .slice(0, 12);
  }, [natInput, natOptions]);
  const pickNat = (c) => {
    setNatInput(natLabel(c));
    setNatOpen(false);
    setNatHi(-1);
  };
  const resolveNat = () => {
    const t = natInput.trim();
    if (!t) return null;
    // 1) 라벨 끝의 (XX) 코드 — datalist 선택 시
    const mCode = t.match(/\(([A-Za-z]{2})\)\s*$/);
    if (mCode) {
      const hit = natOptions.find((c) => c.nat === mCode[1].toUpperCase());
      if (hit) return hit.nat;
    }
    // 2) 2자리 코드 직접 입력
    const byCode = natOptions.find((c) => c.nat === t.toUpperCase());
    if (byCode) return byCode.nat;
    // 3) 한글명 — 정확 일치 우선, 유일 부분일치 허용 (예: "미국", "베트남")
    const koExact = natOptions.filter((c) => COUNTRY_KO[c.nat] === t);
    if (koExact.length === 1) return koExact[0].nat;
    const koPart = natOptions.filter((c) => (COUNTRY_KO[c.nat] || "").includes(t));
    if (koPart.length === 1) return koPart[0].nat;
    // 4) 영문명 — 유일 부분일치
    const byName = natOptions.filter((c) =>
      (c.nat_nm || "").toUpperCase().includes(t.toUpperCase()));
    return byName.length === 1 ? byName[0].nat : null;
  };

  const calc = async () => {
    const nat = resolveNat();
    const w = parseFloat(weight);
    setErr(null);
    setResult(null);
    if (!nat) { setErr("국가를 목록에서 선택해 주세요 (한글명·영문명 또는 2자리 코드)."); return; }
    if (!Number.isFinite(w) || w <= 0) { setErr("중량(kg)을 입력해 주세요."); return; }
    setBusy(true);
    setCmp(null);
    if (type === "COMPARE") {
      try {
        const r = await fetch(`/api/tools/courier-freight/compare?nat=${nat}&weight=${w}`);
        const d = await r.json();
        if (!r.ok) setErr("비교 계산에 실패했습니다. 입력을 확인해 주세요.");
        else setCmp(d);
      } catch (e) {
        setErr("네트워크 오류 — 잠시 후 다시 시도해 주세요.");
      } finally { setBusy(false); }
      return;
    }
    try {
      const r = await fetch(
        `/api/tools/courier-freight?type=${type}&nat=${nat}&weight=${w}`);
      const d = await r.json();
      if (!r.ok) {
        setErr(d.errorCode === "NAT_NOT_FOUND"
          ? "해당 요율에 없는 국가입니다."
          : type === "POST" && d.errorCode === "NO_RATES"
            ? "선편소포는 최대 20kg까지 취급합니다."
            : "계산에 실패했습니다. 입력을 확인해 주세요.");
      } else setResult(d);
    } catch (e) {
      setErr("네트워크 오류 — 잠시 후 다시 시도해 주세요.");
    } finally { setBusy(false); }
  };

  const loadTable = async () => {
    if (showTable) { setShowTable(false); return; }
    setShowTable(true);
    if (table?.type === type) return;
    setTable(null);
    try {
      const r = await fetch(`/api/tools/courier-freight/table?type=${type}`);
      setTable(r.ok ? await r.json() : { rows: [] });
    } catch (e) { setTable({ rows: [] }); }
  };

  // 운임표 피벗 — weight 행 × zone 열
  const pivot = useMemo(() => {
    if (!table?.rows?.length) return null;
    const zones = [...new Set(table.rows.map((r) => r.zone))].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true }));
    const byW = new Map();
    for (const r of table.rows) {
      const w = Number(r.weight);
      if (!byW.has(w)) byW.set(w, {});
      byW.get(w)[r.zone] = r;
    }
    return { zones, weights: [...byW.keys()].sort((a, b) => a - b), byW };
  }, [table]);

  return (
    <ApiPortalLayout active="freight" onNavigate={onNavigate} embedded={embedded}>
      <div style={S.page}>
        <h1 style={S.h1}>운임 계산기</h1>
        <p style={S.p}>
          특송(DHL·FEDEX·UPS) 운임과 <b>탁송품 과세운임 · 선편소포 요금</b>을 계산합니다.
          국가를 선택하면 적용 지역이 자동 결정되고, 결과를 수입 세금 계산으로 바로
          이어갈 수 있습니다.
        </p>

        <div style={S.card}>
          <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 130px auto",
            gap: 12, alignItems: "end" }}>
            <div>
              <label style={S.label}>운임 구분</label>
              <select value={type}
                onChange={(e) => { setType(e.target.value); setResult(null); setCmp(null); setShowTable(false); setShowZones(false); }}
                style={S.input}>
                <option value="COMPARE">{TYPE_LABEL.COMPARE}</option>
                {(meta?.carriers || []).map((c) => (
                  <option key={c.type} value={c.type}>{TYPE_LABEL[c.type] || c.type}</option>
                ))}
              </select>
            </div>
            <div style={{ position: "relative" }}>
              <label style={S.label}>도착 국가 (한글·영문·코드)</label>
              <input value={natInput}
                onChange={(e) => { setNatInput(e.target.value); setNatOpen(true); setNatHi(-1); }}
                onFocus={() => setNatOpen(true)}
                onBlur={() => setTimeout(() => setNatOpen(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setNatOpen(true);
                    setNatHi((h) => Math.min(h + 1, natMatches.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setNatHi((h) => Math.max(h - 1, 0));
                  } else if (e.key === "Enter") {
                    if (natOpen && natMatches.length > 0) {
                      e.preventDefault();
                      pickNat(natMatches[natHi >= 0 ? natHi : 0]);
                    } else calc();
                  } else if (e.key === "Escape") {
                    setNatOpen(false);
                  }
                }}
                placeholder="예: 미국 / VIETNAM / US"
                autoComplete="off"
                style={S.input} />
              {natOpen && natMatches.length > 0 && (
                <div
                  style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30,
                    background: "#fff", border: "1px solid #d1d5db", borderRadius: 8,
                    marginTop: 4, maxHeight: 280, overflowY: "auto",
                    boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                  }}
                >
                  {natMatches.map((c, i) => (
                    <div
                      key={`${c.nat}-${i}`}
                      onMouseDown={(e) => { e.preventDefault(); pickNat(c); }}
                      onMouseEnter={() => setNatHi(i)}
                      style={{
                        padding: "8px 12px", fontSize: 13.5, cursor: "pointer",
                        background: i === natHi ? "#f3f4f6" : "#fff",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}
                    >
                      {COUNTRY_KO[c.nat] ? <b>{COUNTRY_KO[c.nat]}</b> : null}
                      {COUNTRY_KO[c.nat] ? " — " : ""}
                      {c.nat_nm} ({c.nat})
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={S.label}>중량 (kg)</label>
              <input type="number" min="0.1" step="0.1" value={weight}
                onChange={(e) => setWeight(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") calc(); }}
                placeholder="3.5" style={S.input} />
            </div>
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} onClick={calc} disabled={busy}>
              {busy ? "계산 중…" : "운임 계산"}
            </button>
          </div>
          {carrier && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>
              요율 기준일 {fmtDt(carrier.base_dt)}
              {carrier.fuel_per != null ? ` · 유류할증비율 ${carrier.fuel_per}%` : ""}
              {" · "}대상 국가 {natOptions.length}개
              {" · "}
              <span style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setShowZones(!showZones)}>
                적용지역별 국가명 {showZones ? "닫기 ▲" : "보기 ▼"}
              </span>
            </div>
          )}
          {TYPE_DESC[type] && (
            <div style={{ marginTop: 6, fontSize: 12.5, color: "#475569",
              background: "#f8fafc", borderLeft: "3px solid #94a3b8",
              padding: "8px 12px", borderRadius: 4 }}>
              {TYPE_DESC[type]}
            </div>
          )}
          {showZones && carrier && (
            <div style={{ marginTop: 10, border: "1px solid #e5e7eb", borderRadius: 8,
              padding: "10px 14px", fontSize: 12.5, maxHeight: 260, overflowY: "auto" }}>
              {Object.entries(
                natOptions.reduce((m, c) => {
                  (m[c.area] = m[c.area] || []).push(c);
                  return m;
                }, {}),
              )
                .sort(([a], [b]) => String(a).localeCompare(String(b), undefined, { numeric: true }))
                .map(([area, list]) => (
                  <div key={area} style={{ marginBottom: 6 }}>
                    <b style={{ color: "#111827" }}>{area}</b>{" "}
                    <span style={{ color: "#6b7280" }}>
                      {list.map((c) => COUNTRY_KO[c.nat] || c.nat_nm).join(", ")}
                    </span>
                  </div>
                ))}
            </div>
          )}
          {err && <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>{err}</div>}

          {result && (
            <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 10,
              padding: "14px 16px", background: "#f8fafc" }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                {result.type} · {COUNTRY_KO[result.nat] || result.nat_nm} ({result.nat}) ·{" "}
                {result.zone} · {result.weight}kg · 기준일 {fmtDt(result.base_dt)}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px", fontSize: 14 }}>
                <span>무게운임 <b>{won(result.fre_amt)}</b></span>
                <span>유류할증료 <b>{won(result.fuel_amt)}</b></span>
                <span>급증수수료 <b>{won(result.surge_fee)}</b></span>
                <span style={{ marginLeft: "auto", fontSize: 17, fontWeight: 800 }}>
                  총 운임 {won(result.total)}
                </span>
              </div>
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                {OFFICIAL_LINKS[result.type] && (
                  <a href={encodeURI(OFFICIAL_LINKS[result.type][1])} target="_blank"
                    rel="noopener noreferrer" style={{ fontSize: 12.5, color: "#6b7280" }}>
                    {OFFICIAL_LINKS[result.type][0]} ↗
                  </a>
                )}
                <button
                  onClick={() => {
                    sessionStorage.setItem("taxcalc_prefill",
                      JSON.stringify({ freight: result.total }));
                    onNavigate("taxcalc");
                  }}
                  style={{ marginLeft: "auto", background: "#fff", color: "#111827",
                    border: "1.5px solid #111827", borderRadius: 8,
                    padding: "8px 16px", fontSize: 13, fontWeight: 700,
                    cursor: "pointer", fontFamily: "inherit" }}>
                  이 운임으로 수입 세금 계산 →
                </button>
              </div>
            </div>
          )}

          {/* 전체 비교 결과 — 총 운임 오름차순, 최저 강조, 공식 확인 링크 */}
          {cmp && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
                {cmp.nat} · {cmp.weight}kg — 총 운임 낮은 순
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["구분", "지역", "무게운임", "유류할증료", "급증수수료", "총 운임", "공식 확인"].map((h, i) => (
                        <th key={h} style={{ textAlign: i >= 2 && i <= 5 ? "right" : "left",
                          padding: "9px 12px", background: "#f8fafc", fontSize: 12.5,
                          fontWeight: 700, color: "#374151",
                          borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...cmp.results]
                      .sort((a, b) => (a.total ?? Infinity) - (b.total ?? Infinity))
                      .map((r, i) => {
                        const cheapest = i === 0 && r.total != null;
                        const num = (v) => (v == null ? "—" : Number(v).toLocaleString());
                        return (
                          <tr key={r.type}
                            style={cheapest ? { background: "#f0fdf4" } : undefined}>
                            <td style={{ padding: "9px 12px", fontSize: 13.5, fontWeight: 700,
                              whiteSpace: "nowrap" }}>
                              {TYPE_LABEL[r.type] || r.type}
                              {r.source === "official_api" && (
                                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700,
                                  color: "#1d4ed8", background: "#dbeafe",
                                  padding: "1px 7px", borderRadius: 8 }}>공식 실시간</span>
                              )}
                              {r.source === "official_api" && r.service && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                  color: "#9ca3af" }}>
                                  {r.service}{r.negotiated ? " · 계약요율" : ""}
                                </div>
                              )}
                              {r.base_dt && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                  color: "#9ca3af" }}>
                                  요율 {String(r.base_dt).replace(
                                    /^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")} 기준
                                </div>
                              )}
                              {cheapest && (
                                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700,
                                  color: "#137333", background: "#dcfce7",
                                  padding: "1px 7px", borderRadius: 8 }}>최저</span>
                              )}
                            </td>
                            {r.error ? (
                              <td colSpan={5} style={{ padding: "9px 12px", fontSize: 12.5,
                                color: "#9ca3af" }}>
                                {r.type === "POST" && r.error === "no_rates"
                                  ? "선편 미취급(최대 20kg)"
                                  : r.error === "nat_not_found"
                                    ? "해당 국가 요율 없음" : "계산 불가"}
                              </td>
                            ) : (
                              <>
                                <td style={{ padding: "9px 12px", fontSize: 13 }}>{r.zone}</td>
                                {[r.fre_amt, r.fuel_amt, r.surge_fee].map((v, j) => (
                                  <td key={j} style={{ padding: "9px 12px", fontSize: 13,
                                    textAlign: "right",
                                    fontFamily: "ui-monospace,monospace" }}>{num(v)}</td>
                                ))}
                                <td style={{ padding: "9px 12px", fontSize: 14, fontWeight: 800,
                                  textAlign: "right",
                                  fontFamily: "ui-monospace,monospace" }}>
                                  {num(r.total)}{r.currency && r.currency !== "KRW"
                                    ? ` ${r.currency}` : "원"}
                                </td>
                              </>
                            )}
                            <td style={{ padding: "9px 12px", fontSize: 12.5, whiteSpace: "nowrap" }}>
                              {OFFICIAL_LINKS[r.type] && (
                                <a href={encodeURI(OFFICIAL_LINKS[r.type][1])} target="_blank"
                                  rel="noopener noreferrer">
                                  {OFFICIAL_LINKS[r.type][0]} ↗
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: "#664d03",
                background: "#fef7e0", borderRadius: 6, padding: "8px 12px" }}>
                ⚠ <b style={{ color: "#1d4ed8" }}>공식 실시간</b> 표시 행은 특송사
                공식 Rate API에서 방금 받은 견적입니다. 그 외 특송 금액은 당사 보유
                요율표(각 기준일 표기) 기준의 <b>참고 견적</b>이며, 공식 사이트 견적은
                부피중량·실시간 유류할증·계약 할인에 따라 다를 수 있으니 발송 확정 전
                공식 확인 링크에서 검증하세요. 탁송품 과세운임·선편소포는
                관세청·우체국 고시 기준입니다.
              </div>
              <div style={{ marginTop: 8, textAlign: "right" }}>
                {(() => {
                  const best = [...cmp.results].filter((r) => r.total != null)
                    .sort((a, b) => a.total - b.total)[0];
                  return best ? (
                    <button
                      onClick={() => {
                        sessionStorage.setItem("taxcalc_prefill",
                          JSON.stringify({ freight: best.total }));
                        onNavigate("taxcalc");
                      }}
                      style={{ background: "#111827", color: "#fff", border: "none",
                        borderRadius: 8, padding: "9px 18px", fontSize: 13,
                        fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      최저 운임({TYPE_LABEL[best.type]})으로 세금 계산 →
                    </button>
                  ) : null;
                })()}
              </div>
            </div>
          )}
        </div>

        {type !== "COMPARE" && (
        <div style={{ marginTop: 14 }}>
          <button onClick={loadTable}
            style={{ ...S.btn, background: "#fff", color: "#111827",
              border: "1.5px solid #d1d5db" }}>
            {showTable ? "운임표 닫기 ▲" : `${TYPE_LABEL[type] || type} 운임표 보기 ▼`}
          </button>
        </div>
        )}
        {showTable && (
          <div style={S.card}>
            {!pivot ? (
              <div style={{ fontSize: 13, color: "#6b7280" }}>불러오는 중…</div>
            ) : (
              /* 스크롤러(패딩 0) — sticky 헤더가 이 박스 상단에 붙어 행 비침 없음 */
              <div
                style={{
                  maxHeight: 460, overflow: "auto",
                  border: "1px solid #e5e7eb", borderRadius: 8,
                }}
              >
                <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, textAlign: "left", left: 0, zIndex: 3 }}>
                        중량(kg)
                      </th>
                      {pivot.zones.map((z) => <th key={z} style={S.th}>{z}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {pivot.weights.map((w) => (
                      <tr key={w}>
                        <td
                          style={{
                            ...S.td, textAlign: "left", fontWeight: 600,
                            position: "sticky", left: 0, zIndex: 1,
                            background: "#f8fafc",
                            boxShadow: "inset -1px 0 0 #e5e7eb",
                          }}
                        >
                          {w >= 44 ? `${w}~ (kg당)` : w}
                        </td>
                        {pivot.zones.map((z) => {
                          const r = pivot.byW.get(w)[z];
                          return (
                            <td key={z} style={S.td}>
                              {r ? Number(r.fre_amt).toLocaleString() : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>
              표 값은 무게운임(원). 44kg 이상 행은 초과 구간의 kg 당 단가입니다.
              유류할증료·급증수수료는 계산기에서 합산됩니다.
            </div>
          </div>
        )}

        <div style={S.note}>
          참고용 요율입니다 — 실제 청구액은 부피중량(용적)·계약 할인·잔존 유류할증 변동에
          따라 달라질 수 있습니다. 요율 기준일을 확인하시고, 정확한 견적은{" "}
          <a href="/?page=contact"
            onClick={(e) => { e.preventDefault(); onNavigate("contact"); }}>문의하기</a>로
          요청해 주세요.
        </div>
      </div>
    </ApiPortalLayout>
  );
}
